// pages/tests/MonthlyTestMarks.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  PersonOff as AbsentIcon
} from '@mui/icons-material';
import { data, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { getAcademicYear } from '../../utils/academicYear';
import { marksApi, studentsApi, testsApi } from './api/apiClientMonthly';

const MonthlyTestMarks = ({ user }) => {
  const navigate = useNavigate();
  const { id, standard } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [test, setTest] = useState(null);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [hasChanges, setHasChanges] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [testRes, studentsRes, savedMarksRes] = await Promise.all([
        testsApi.getById(id),
        studentsApi.getAll(`${user?.mas_code}/${getAcademicYear(new Date())}/${standard}`),
        marksApi.getMarks(id),  // returns [{studentId, marksObtained, remarks, absent}]
      ]);

      const testData = testRes?.data ?? testRes;

      const studentList = Array.isArray(studentsRes?.data)
        ? studentsRes.data
        : Array.isArray(studentsRes)
          ? studentsRes
          : [];

      const savedByStudentId = new Map(
        (savedMarksRes?.data ?? []).map((m) => [String(m.studentId), m])
      );

      const mergedList = studentList.map((apiStudent, idx) => {
        const sid = String(apiStudent.studentId ?? apiStudent.id);
        const saved = savedByStudentId.get(sid);
        return {
          studentId: apiStudent.studentId ?? apiStudent.id,
          rollNumber: apiStudent.rollNumber ?? "",
          studentName: apiStudent.studentName ?? "",
          marksObtained: saved?.marksObtained ?? null,
          remarks: saved?.remarks ?? "",
          absent: saved?.absent ?? false,
          _seq: idx + 1,
        };
      });

      setTest(testData);
      setStudents(mergedList);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate grade based on percentage
  const calculateGrade = (marks, maxMarks) => {
    if (marks === null || marks === undefined || marks === '') return '';
    const percentage = (parseFloat(marks) / maxMarks) * 100;
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 35) return 'D';
    return 'F';
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A+': 'success',
      'A': 'success',
      'B+': 'info',
      'B': 'info',
      'C': 'warning',
      'D': 'warning',
      'F': 'error'
    };
    return colors[grade] || 'default';
  };

  // Update marks for a student
  const handleMarksChange = (studentId, value) => {
    if (value !== '' && (parseFloat(value) < 0 || parseFloat(value) > test.maxMarks)) {
      return;
    }

    setStudents(prev => prev.map(s =>
      s.studentId === studentId
        ? { ...s, marksObtained: value === '' ? '' : parseFloat(value) }
        : s
    ));
    setHasChanges(true);
  };

  const handleRemarksChange = (studentId, value) => {
    setStudents(prev => prev.map(s =>
      s.studentId === studentId ? { ...s, remarks: value } : s
    ));
    setHasChanges(true);
  };

  const handleAbsentToggle = (studentId) => {
    setStudents(prev => prev.map(s =>
      s.studentId === studentId
        ? { ...s, absent: !s.absent, marksObtained: !s.absent ? 0 : s.marksObtained }
        : s
    ));
    setHasChanges(true);
  };

  const handleMarkAllAbsent = (studentId) => {
    // Mark all currently filtered students as absent
    const filteredIds = filteredStudents.map(s => s.studentId);
    setStudents(prev => prev.map(s =>
      filteredIds.includes(s.studentId) ? { ...s, absent: true, marksObtained: 0 } : s
    ));
    setHasChanges(true);
  };

  // Filter students
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      const matchesSearch =
        student.studentName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        student.rollNumber?.toLowerCase().includes(searchTerm?.toLowerCase());

      const matchesFilter =
        filterStatus === 'ALL' ||
        (filterStatus === 'ENTERED' && student.marksObtained !== null && student.marksObtained !== '') ||
        (filterStatus === 'PENDING' && (student.marksObtained === null || student.marksObtained === '')) ||
        (filterStatus === 'PASS' && !student.absent && student.marksObtained >= test.passingMarks) ||
        (filterStatus === 'FAIL' && !student.absent && student.marksObtained !== null && student.marksObtained < test.passingMarks) ||
        (filterStatus === 'ABSENT' && student.absent);

      return matchesSearch && matchesFilter;
    });
  }, [students, searchTerm, filterStatus, test]);

  // Statistics
  const stats = useMemo(() => {
    const entered = students.filter(s => s.marksObtained !== null && s.marksObtained !== '').length;
    const absent = students.filter(s => s.absent).length;
    const passed = students.filter(s => !s.absent && s.marksObtained !== null && s.marksObtained >= test?.passingMarks).length;
    const failed = students.filter(s => !s.absent && s.marksObtained !== null && s.marksObtained < test?.passingMarks).length;

    return {
      total: students.length,
      entered,
      pending: students.length - entered - absent,
      absent,
      passed,
      failed
    };
  }, [students, test]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const marksData = students
        .filter(s => s.marksObtained !== null && s.marksObtained !== '')
        .map(s => ({
          studentId: s.studentId,
          rollNumber: s.rollNumber,
          studentName: s.studentName,
          marksObtained: s.absent ? 0 : s.marksObtained,
          grade: s.absent ? 'F' : calculateGrade(s.marksObtained, test.maxMarks),
          remarks: s.remarks,
          absent: s.absent
        }));

      marksApi.saveMarks(id, marksData)
      setTimeout(() => {
        setSaving(false);
        setHasChanges(false);
        setSaveDialogOpen(false);
        setSnackbar({ open: true, message: 'Marks saved successfully!', severity: 'success' });
        navigate(`/tests/${id}`);
      }, 800);
    } catch (error) {
      setSaving(false);
      setSnackbar({ open: true, message: 'Failed to save marks', severity: 'error' });
    }
  };

  const handleReset = () => {
    fetchData();
    setHasChanges(false);
    setResetDialogOpen(false);
    setSnackbar({ open: true, message: 'Changes reset', severity: 'info' });
  };

  const handleBack = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(`/tests/${id}`);
      }
    } else {
      navigate(`/tests/${id}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!test) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Test not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              Enter Marks
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {test.testName} • {test.standard} - {test.section} • {test.subject} • {format(new Date(test.testDate), 'dd MMM yyyy')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => setResetDialogOpen(true)}
            disabled={!hasChanges || saving}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => setSaveDialogOpen(true)}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save Marks'}
          </Button>
        </Box>
      </Box>

      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
          You have unsaved changes. Don't forget to save!
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography color="textSecondary" variant="caption">Total</Typography>
              <Typography variant="h5">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography color="textSecondary" variant="caption">Entered</Typography>
              <Typography variant="h5" color="success.main">{stats.entered}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography color="textSecondary" variant="caption">Pending</Typography>
              <Typography variant="h5" color="warning.main">{stats.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography color="textSecondary" variant="caption">Absent</Typography>
              <Typography variant="h5" color="error.main">{stats.absent}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography color="textSecondary" variant="caption">Passed</Typography>
              <Typography variant="h5" color="success.main">{stats.passed}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography color="textSecondary" variant="caption">Failed</Typography>
              <Typography variant="h5" color="error.main">{stats.failed}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or roll no..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Filter"
              >
                <MenuItem value="ALL">All Students</MenuItem>
                <MenuItem value="ENTERED">Marks Entered</MenuItem>
                <MenuItem value="PENDING">Marks Pending</MenuItem>
                <MenuItem value="PASS">Passed</MenuItem>
                <MenuItem value="FAIL">Failed</MenuItem>
                <MenuItem value="ABSENT">Absent</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
              >
                Template
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadIcon />}
                component="label"
              >
                Import
                <input type="file" hidden accept=".csv,.xlsx" />
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<AbsentIcon />}
                onClick={handleMarkAllAbsent}
              >
                Mark Filtered Absent
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Marks Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell width="60">#</TableCell>
              <TableCell width="100">Roll No</TableCell>
              <TableCell>Student Name</TableCell>
              <TableCell align="center" width="150">Marks Obtained</TableCell>
              <TableCell align="center" width="100">Grade</TableCell>
              <TableCell align="center" width="100">Status</TableCell>
              <TableCell width="200">Remarks</TableCell>
              <TableCell align="center" width="80">Absent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStudents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    No students found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredStudents.map((student, index) => {
                const grade = student.absent ? 'F' : calculateGrade(student.marksObtained, test.maxMarks);
                const isPassed = !student.absent && student.marksObtained !== null &&
                  student.marksObtained !== '' && student.marksObtained >= test.passingMarks;
                const isFailed = !student.absent && student.marksObtained !== null &&
                  student.marksObtained !== '' && student.marksObtained < test.passingMarks;

                return (
                  <TableRow key={student.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{student.rollNumber}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {student.studentName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={student.absent ? 0 : (student.marksObtained ?? '')}
                        onChange={(e) => handleMarksChange(student.studentId, e.target.value)}
                        disabled={student.absent}
                        slotProps={{
                          htmlInput: {
                            min: 0,
                            max: test.maxMarks,
                            style: { textAlign: 'center' }
                          }
                        }}
                        sx={{ width: 100 }}
                        placeholder="--"
                      />
                      <Typography variant="caption" display="block" color="textSecondary">
                        / {test.maxMarks}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {grade ? (
                        <Chip
                          label={grade}
                          color={getGradeColor(grade)}
                          size="small"
                        />
                      ) : (
                        <Typography variant="caption" color="textSecondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {student.absent ? (
                        <Chip label="Absent" color="error" size="small" />
                      ) : isPassed ? (
                        <Chip
                          label="Pass"
                          color="success"
                          size="small"
                          icon={<CheckCircleIcon />}
                        />
                      ) : isFailed ? (
                        <Chip
                          label="Fail"
                          color="error"
                          size="small"
                          icon={<CancelIcon />}
                        />
                      ) : (
                        <Typography variant="caption" color="textSecondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={student.remarks || ''}
                        onChange={(e) => handleRemarksChange(student.studentId, e.target.value)}
                        placeholder="Optional remarks"
                        disabled={student.absent}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={student.absent}
                        onChange={() => handleAbsentToggle(student.studentId)}
                        color="error"
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bottom Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Test
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => setSaveDialogOpen(true)}
          disabled={!hasChanges || saving}
          size="large"
        >
          {saving ? 'Saving...' : 'Save All Marks'}
        </Button>
      </Box>

      {/* Save Confirmation Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Marks</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to save marks for <strong>{stats.entered}</strong> students.
            {stats.pending > 0 && (
              <>
                <br /><br />
                <Alert severity="warning">
                  <strong>{stats.pending}</strong> student(s) still have pending marks.
                  You can save now and enter them later.
                </Alert>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save Marks'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to discard all unsaved changes? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReset} color="error" variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MonthlyTestMarks;